import {useEffect, useTransition} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {receiveRoute} from 'coreStore/data/page';
import {isShellFlushed, reportLateHeader} from 'coreUtils/ssrShell';

import {buildDeferredRoute, resolvePageDeferreds} from './resolvePageDeferreds';
import useClientServerEffect from './useClientServerEffect';
import {CURRENT_ROUTE_KEY} from './useCurrentRoute';
import {resolveDeferred} from './useDeferredData';
import useServerData from './useServerData';
import useSSRResponse from './useSSRResponse';

import {ISeoMetadata, IPageUrls, IJsonLd, ILink, ReceiveRoutePayload} from 'coreStore/data/page/types';
import {MAX_CACHE_AGE} from '../../server/constants';

const buildReceiveRoutePayload = (
	route: string,
	params: ReceiveRoutePayload['params'] | undefined,
	isHomePage: boolean,
): ReceiveRoutePayload => {
	const payload: ReceiveRoutePayload = {
		currentRoute: route,
		isHomePage,
	};

	if (params) {
		payload.params = params;
	}

	return payload;
};

export interface PageInitConfig {
	route: string;
	params?: ReceiveRoutePayload['params'];
	isHomePage?: boolean;
	seo?: {
		metadata?: ISeoMetadata[];
		pageUrls?: string | IPageUrls;
		jsonld?: IJsonLd;
		canonical?: string;
		links?: ILink[];
	};
	statusCode?: number;
	cache?: number;
}

/**
 * React hook for **simple pages** that only need basic SEO + route dispatch.
 *
 * Creates its own `useServerData` query internally, so the consuming component
 * does NOT need to call `useServerData` separately for page-init concerns.
 * Use this when the page has no complex data-fetching queryFn of its own.
 *
 * Also resolves the `current-route` deferred so that shell components
 * (Header, Footer) can read the route via `useDeferredRoute()`.
 * Works on both SSR and CSR.
 *
 * For pages that already have a `useServerData` queryFn (e.g. CLP, PDP,
 * CategoryPage) and need full SEO control (jsonld, canonical, multiple
 * metadata entries), use {@link usePageInit} inside the queryFn instead.
 */
const usePageInit = (config: PageInitConfig): void => {
	const queryClient = useQueryClient();
	const res = useSSRResponse();
	const {managerRepository} = useGeneralProvider();
	const isHomePage = config.isHomePage ?? false;
	const [, startTransition] = useTransition();

	useEffect(() => {
		startTransition(() => {
			managerRepository.getStoreMgr().getStore().dispatch(
				receiveRoute(buildReceiveRoutePayload(config.route, config.params, isHomePage)),
			);
		});

		resolveDeferred(
			queryClient,
			CURRENT_ROUTE_KEY,
			buildDeferredRoute(config.route, isHomePage),
		);
	}, [config.route, isHomePage]);

	useServerData<null>(`${config.route}-init`, async () => {
		if (res) {
			if (config.statusCode) {
				if (isShellFlushed(res)) {
					reportLateHeader(res, `status ${config.statusCode}`);
				} else {
					res.statusCode = config.statusCode;
				}
			}

			res.setCache?.(config.cache || MAX_CACHE_AGE);
		}

		// Yield before Redux dispatches so they run outside the synchronous
		// render phase of useSuspenseQuery, avoiding "Cannot update component
		// X while rendering component Y" warnings.
		await Promise.resolve();

		managerRepository.getStoreMgr().getStore().dispatch(
			receiveRoute(buildReceiveRoutePayload(config.route, config.params, isHomePage)),
		);

		return null;
	}, [config]);

	// SEO is applied on every mount, not in the queryFn: a cached query (client back/forward
	// navigation) does not run its queryFn again. Runs during render on the server, before
	// `resolvePageDeferreds` releases `SuspenseSEO`. Pages without `seo` manage it themselves.
	useClientServerEffect(() => {
		if (config.seo) {
			managerRepository.getSeoMgr().replaceData(config.seo);
		}

		resolvePageDeferreds(queryClient, config.route, isHomePage);
	}, [config.route, isHomePage, JSON.stringify(config.seo ?? null)]);
};

export default usePageInit;
